import { z } from 'zod';

const ZERO_KM_MESSAGE =
  'Informe a data de saída da nota fiscal para equipamento Zero KM';

const CURRENT_YEAR = new Date().getFullYear();

/**
 * Step 2 — Adicionar equipamento (RD Equipamentos, ramo 0171, fluxo
 * individual).
 *
 * Valida o formulário já NORMALIZADO — `year` como number e `value` em
 * centavos — espelhando `RdEquipmentPayload`
 * (`@/services/quotes/rd-equipamentos/types`) campo a campo. O componente
 * (`step-equipamentos.component.tsx`) converte os valores brutos do
 * formulário (strings mascaradas, ver `StepEquipamentoFormValues` abaixo)
 * para este formato antes de chamar `safeParse` (ver
 * `use-add-rd-equipment.hook.ts`).
 */
export const StepEquipamentoSchema = z
  .object({
    equipmentCode: z.string().min(1, 'Selecione um equipamento'),
    year: z
      .number()
      .int('Informe o ano')
      .min(1900, 'Informe o ano')
      .max(CURRENT_YEAR, 'Ano não pode ser futuro'),
    value: z.number().int().min(1, 'Informe o valor do equipamento'),
    // Marca, modelo e chassi identificam o bem na apólice e em sinistro —
    // obrigatórios no formulário do Portal (decisão de PO). O motor os mantém
    // opcionais no input model: a API one-shot e o wizard do demo continuam
    // aceitando itens sem eles.
    brand: z.string().trim().min(1, 'Informe a marca'),
    model: z.string().trim().min(1, 'Informe o modelo'),
    // Não entra no cálculo — comprovação em caso de sinistro. Obrigatório já
    // no cadastro do equipamento (decisão PO): o motor exige o campo em todos
    // os itens ao efetivar a proposta, então cobramos desde já.
    serialNumber: z.string().trim().min(1, 'Informe o chassi / nº de série'),
    isZeroKm: z.boolean(),
    nfNumber: z.string().optional(),
    nfDate: z.string().optional(),
    nfIssuer: z.string().optional(),
    // Endereço do risco DESTE item. A obrigatoriedade depende de
    // `modality.requiresLocation`, que só existe em runtime (via query) — por
    // isso fica no validador de campo do componente, não aqui, seguindo a
    // mesma decisão documentada em `step-parametros.schema.ts`.
    riskCep: z.string().optional(),
    riskAddress: z.string().optional(),
    riskNumber: z.string().optional(),
    riskComplement: z.string().optional(),
    riskDistrict: z.string().optional(),
    riskCity: z.string().optional(),
    riskState: z.string().max(2).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.isZeroKm) return;

    // Só a DATA DE SAÍDA é obrigatória: é ela que comprova que o equipamento
    // é novo. Número e emissor viraram opcionais (decisão de PO) — antes os
    // três eram exigidos juntos, aqui e no motor.
    if (!data.nfDate?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['nfDate'],
        message: ZERO_KM_MESSAGE,
      });
    }
  });

/** Formato normalizado (payload-ready) — ver nota acima. */
export type StepEquipamentoValues = z.infer<typeof StepEquipamentoSchema>;

/**
 * Formato bruto do formulário de adicionar equipamento — `year`/`value`
 * ficam como string (inputs controlados: `NumberField`/máscara de moeda) e
 * só são convertidos para `StepEquipamentoValues` no submit.
 */
export type StepEquipamentoFormValues = {
  equipmentCode: string;
  year: string;
  value: string;
  brand: string;
  model: string;
  serialNumber: string;
  isZeroKm: boolean;
  nfNumber: string;
  nfDate: string;
  nfIssuer: string;
  riskCep: string;
  riskAddress: string;
  riskNumber: string;
  riskComplement: string;
  riskDistrict: string;
  riskCity: string;
  riskState: string;
};
